import React, { useCallback, useMemo, useContext } from 'react';

import { InjectedIntl, injectIntl } from 'react-intl';

import { AppSwitcher } from '@atlaskit/atlassian-navigation';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { Placement } from '@atlassian/post-office-placement-remote';
import RemoteSwitcherPlugin from '@atlassian/remote-module-loader/plugin-switcher';
import { RemoteComponent } from '@atlassian/remote-module-loader/remote-component';
import Switcher from '@atlassian/switcher';

import { HorizontalNavContext } from '../horizontal-nav/horizontal-nav-context';

import messages from './atlassian-switcher.i18n';
import { SwitcherContainer } from './atlassian-switcher.style';

export type AtlassianSwitcherProps = {
  globalItemStyles?: (styles: {}) => {};
  isAtlassianSwitcherOpen: boolean;
  isLoggedIn: boolean;
  onToggleAtlassianSwitcher: (isOpen: boolean) => void;
};

type InjectedAtlassianSwitcherProps = AtlassianSwitcherProps & {
  intl: InjectedIntl;
};

type BaseSwitcherProps = {
  appearance: 'standalone' | 'drawer';
  tenantId?: string;
};

type SwitcherComponentProps = React.ComponentProps<typeof Switcher>;

type DiscoveryExposureAttributes = {
  locale: string | undefined;
  ffEnableOverFiftyUsers: boolean | undefined;
  ffAllLocales: boolean | undefined;
};

type SwitcherDiscoveryExposureEvent = {
  actionSubject: 'section';
  action: 'viewed';
  actionSubjectId: 'switcherDiscoveryExposure';
  source: 'atlassianSwitcher';
  attributes: {
    locale: DiscoveryExposureAttributes['locale'];
    featureFlags: {
      ffEnableOverFiftyUsers: DiscoveryExposureAttributes['ffEnableOverFiftyUsers'];
      ffAllLocales: DiscoveryExposureAttributes['ffAllLocales'];
    };
  };
};

export const createSwitcherDiscoveryExposureEvent = ({
  locale,
  ffEnableOverFiftyUsers,
  ffAllLocales,
}: DiscoveryExposureAttributes): SwitcherDiscoveryExposureEvent => ({
  actionSubject: 'section',
  action: 'viewed',
  actionSubjectId: 'switcherDiscoveryExposure',
  source: 'atlassianSwitcher',
  attributes: {
    locale,
    featureFlags: {
      ffEnableOverFiftyUsers,
      ffAllLocales,
    },
  },
});

/* eslint @typescript-eslint/ban-types: "warn" */
export const BaseSwitcher: React.FC<BaseSwitcherProps> = React.memo(props => {
  const { appearance } = props;
  const { environment } = useContext(HorizontalNavContext);

  const remoteSwitcherPlugin = useMemo(() => {
    return new RemoteSwitcherPlugin({
      env: environment === 'production' ? 'production' : 'staging',
      variant: 'latest',
    });
  }, [environment]);

  const isPostOfficeIntegrationEnabled = FeatureGates.checkGate(
    StatsigFeatureKeys.postOfficeIntegrationInAppSwitcher
  );
  const switcherProps = useMemo<SwitcherComponentProps>(
    () => ({
      appearance,
      product: 'bitbucket',
      disableCustomLinks: true,
      discoverySection: isPostOfficeIntegrationEnabled ? (
        <Placement placementId="app-switcher-discovery-section" />
      ) : undefined,
    }),
    [appearance, isPostOfficeIntegrationEnabled]
  );

  return (
    <SwitcherContainer>
      <RemoteComponent<SwitcherComponentProps>
        remotePlugin={remoteSwitcherPlugin}
        analyticsChannel="navigation"
        ErrorFallback={<Switcher {...switcherProps} />}
        {...switcherProps}
      />
    </SwitcherContainer>
  );
});

export const SwitcherButton = injectIntl(
  ({
    onToggleAtlassianSwitcher,
    isSelected,
    intl,
  }: Omit<
    InjectedAtlassianSwitcherProps,
    'isAtlassianSwitcherOpen' | 'isLoggedIn'
  > & { isSelected?: boolean }) => {
    const traceInteraction = usePressTracing('atlassian-switcher-open');

    const onClick = useCallback(() => {
      traceInteraction();
      onToggleAtlassianSwitcher(true);
    }, [onToggleAtlassianSwitcher, traceInteraction]);

    return (
      <>
        <AppSwitcher
          id="appSwitcher"
          testId="app-switcher"
          isSelected={isSelected}
          onClick={onClick}
          tooltip={intl.formatMessage(messages.tooltip)}
        />
      </>
    );
  }
);
